// Experience status enum matching backend
export type ExperienceStatus = 'DRAFT' | 'AWAITING' | 'CONFIRMED' | 'REJECTED';

// Experience source enum matching backend
export type ExperienceSource = 'MANUAL';

// DateInfo matching backend DateInfoDto
export interface DateInfo {
  year: number;
  month?: number | null;
}

// Experience matching backend ExperienceResponse DTO
export type ExperienceVerificationStatus = 'DRAFT' | 'PENDING' | 'VERIFIED' | 'REVIEW' | 'REJECTED';

export interface Experience {
  id: string;
  candidateId: string;
  companyId: string;
  companyName: string;
  jobTitle: string;
  startDate?: DateInfo | null;
  endDate?: DateInfo | null;
  isOngoing: boolean;
  isFullTime: boolean;
  isContractor: boolean;
  status: ExperienceStatus;
  verificationStatus: ExperienceVerificationStatus;
  confirmedReferenceCount: number;
  source: ExperienceSource;
  continuous: boolean;
  isGig: boolean;
  effectiveWorkMonths: number;
  tags: string[];
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

// Request DTOs matching backend
export interface CreateExperienceRequest {
  companyName: string;
  jobTitle: string;
  startDate?: DateInfo | null;
  endDate?: DateInfo | null;
  isOngoing?: boolean;
  isFullTime?: boolean;
  isContractor?: boolean;
  continuous?: boolean;
  isGig?: boolean;
  tags?: string[];
}

export interface UpdateExperienceRequest {
  companyName?: string;
  jobTitle?: string;
  startDate?: DateInfo | null;
  endDate?: DateInfo | null;
  isOngoing?: boolean;
  isFullTime?: boolean;
  isContractor?: boolean;
  continuous?: boolean;
  isGig?: boolean;
  tags?: string[];
}

export interface UpdateExperienceStatusRequest {
  status: ExperienceStatus;
}

// Query parameters for listing experiences.
// Filtering and sorting are done client-side (see useTableFilters).
export interface ListExperiencesParams {
  candidateId: string;
}

// Status display configuration
export const EXPERIENCE_STATUS_CONFIG: Record<
  ExperienceStatus,
  { label: string; color: 'default' | 'warning' | 'success' | 'destructive' }
> = {
  DRAFT: { label: 'Draft', color: 'default' },
  AWAITING: { label: 'Awaiting', color: 'warning' },
  CONFIRMED: { label: 'Confirmed', color: 'success' },
  REJECTED: { label: 'Rejected', color: 'destructive' },
};

// Status transition rules (which transitions are allowed)
export const EXPERIENCE_STATUS_TRANSITIONS: Record<ExperienceStatus, ExperienceStatus[]> = {
  DRAFT: ['AWAITING', 'REJECTED'],
  AWAITING: ['CONFIRMED', 'REJECTED'],
  CONFIRMED: ['REJECTED'],
  REJECTED: ['DRAFT'],
};

// Helper function to format DateInfo for display
export function formatDateInfo(dateInfo: DateInfo | null | undefined): string {
  if (!dateInfo) return '';
  if (dateInfo.month) {
    const monthStr = dateInfo.month.toString().padStart(2, '0');
    return `${dateInfo.year}-${monthStr}`;
  }
  return dateInfo.year.toString();
}

// Helper function to calculate duration string
export function calculateDuration(
  startDate: DateInfo | null | undefined,
  endDate: DateInfo | null | undefined,
  isOngoing: boolean
): string {
  if (!startDate) return '';

  const start = new Date(startDate.year, (startDate.month || 1) - 1);
  const end = isOngoing
    ? new Date()
    : endDate
      ? new Date(endDate.year, (endDate.month || 1) - 1)
      : new Date();

  const months =
    (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years === 0) {
    return `${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
  }
  if (remainingMonths === 0) {
    return `${years} year${years !== 1 ? 's' : ''}`;
  }
  return `${years} year${years !== 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
}
